import { apiClient } from './api'
import { UserProfile, UpdateProfileRequest, ProfileResponse } from '@/types/profile'
import { API_CONFIG } from '@/constants'

export class ProfileService {
  private readonly baseUrl = '/user'

  async getProfile(): Promise<UserProfile> {
    const response = await apiClient.get<ProfileResponse>(`${this.baseUrl}/profile`)
    if (!response.data) {
      throw new Error('Failed to fetch profile data')
    }
    return response.data.data
  }

  async updateProfile(profileData: UpdateProfileRequest): Promise<UserProfile> {
    const response = await apiClient.put<ProfileResponse>(`${this.baseUrl}/profile`, profileData)
    if (!response.data) {
      throw new Error('Failed to update profile data')
    }
    return response.data.data
  }

  /**
   * Upload profile picture using multipart/form-data
   * Supports JPEG, PNG, WebP formats up to 25MB
   */
  async uploadProfilePicture(file: File): Promise<UserProfile> {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload JPEG, PNG, or WebP image.')
    }

    // Validate file size (25MB max)
    const maxSize = 25 * 1024 * 1024 // 25MB in bytes
    if (file.size > maxSize) {
      throw new Error('File size exceeds 25MB limit.')
    }

    const formData = new FormData()
    formData.append('profile_picture', file)

    const token = localStorage.getItem('token')
    
    const response = await fetch(`${API_CONFIG.BASE_URL}${this.baseUrl}/profile`, {
      method: 'PUT',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
        // Note: Don't set Content-Type for FormData, browser will set it automatically with boundary
      },
      body: formData
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || 'Failed to upload profile picture')
    }

    const result = await response.json() as ProfileResponse
    return result.data
  }
}

export const profileService = new ProfileService()
